import { Injectable, inject } from '@angular/core';
import { Store, createSelector } from '@ngrx/store';
import { Observable } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { FavoriteOrganizationsService } from 'wflow-main-app/src/app/pages/dashboard/features/org-card/services/org-card/org-favorite.service';
import {
  ContextItem,
  ContextSelector,
  OrgGroup,
} from 'wflow-main-app/src/app/shared/components/page-container';
import { partitionOrgsByGroup } from 'wflow-main-app/src/app/shared/utils/org-grouping.utils';
import { OrganizationsStore } from 'wflow-main-app/src/app/store/organizations/organizations.model';
import {
  findOrganization,
  selectCurrentOrganization,
  selectOrganizationsCount,
} from 'wflow-main-app/src/app/store/organizations/organizations.selectors';

const selectOrgName = createSelector(
  selectCurrentOrganization,
  (org) => org?.name ?? '',
);
const selectOrgId = createSelector(
  selectCurrentOrganization,
  (org) => org?.id ?? '',
);

@Injectable({
  providedIn: 'root',
})
export class ContextSelectorResolver implements ContextSelector {
  readonly showSearchFromCount = 5;

  readonly store: Store<OrganizationsStore> = inject(Store);
  readonly currentName = this.store.selectSignal(selectOrgName);
  readonly currentId = this.store.selectSignal(selectOrgId);
  readonly total = this.store.selectSignal(selectOrganizationsCount);
  readonly favorites = inject(FavoriteOrganizationsService).favorites;

  getList(value: string, count: number = 20): Observable<ContextItem[]> {
    return this.store
      .select(
        findOrganization<ContextItem>(value, undefined, false, (org) => ({
          name: org.name,
          path: org.path,
          id: org.id,
        })),
      )
      .pipe(
        take(1),
        map((r) => r.items),
        map((items) => {
          const currentId = this.currentId();
          const excluded = currentId ? new Set([currentId]) : new Set<string>();
          const groups = partitionOrgsByGroup(items, this.favorites(), excluded);
          return [
            ...groups.top.map((i) => ({ ...i, isActive: true, group: 'active' as OrgGroup })),
            ...groups.favorites.map((i) => ({ ...i, group: 'favorites' as OrgGroup })),
            ...groups.others.slice(0, count).map((i) => ({ ...i, group: 'others' as OrgGroup })),
          ];
        }),
      );
  }
}
